<template>
  <div>
    <div
      v-if="showSideNavbar"
      class="navigation-bar"
    >
      <div class="navigation-bar__content">
        <div class="navigation-bar__content-block navigation-bar__content-top">
          <PCNotificationAlertERP
            :primary-icon="false"
            filter="production"
            :title="t('common.alertsAndNotifications')"
          />

          <template
            v-for="(item, index) in visibleTopItems"
            :key="`navigatior-bar-item-top-${index}`"
          >
            <div
              v-if="index > 0"
              class="navigation-bar__separator"
            />

            <PCSideNavbarItem
              :item="item"
              @item-clicked="handleItemClick"
            />
          </template>
        </div>

        <div class="navigation-bar__content-block navigation-bar__content-bottom">
          <PCSideNavbarItem
            v-for="(item, index) in bottomItems"
            :key="`navigatior-bar-item-bottom-${index}`"
            :item="item"
            @item-clicked="handleItemClick"
          />
        </div>
      </div>
    </div>

    <div
      v-else
      class="navigation-bar"
    >
      <div class="navigation-bar__content-only">
        <div class="navigation-bar__content-block navigation-bar__content-bottom">
          <PCSideNavbarItem
            :item="itemExit"
            @item-clicked="handleItemClick"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// @ts-check
import { computed, ref, toRefs } from 'vue';
import { useSideNavbar } from '@/composables/useSideNavbar';
import { useSideNavbarAccess } from '@/composables/useSideNavbarAccess';
import { useScreenOrientation } from '@vueuse/core';
import { f7 } from 'framework7-vue';
import { useI18n } from 'vue-i18n';

import PCNotificationAlertERP from '@/components/common/PCNotificationAlertERP.vue';
import PCSideNavbarItem from '@/components/common/PCSideNavbarItem.vue';
import { useSettingRef } from '@erpagroapp/settings';

/** @import { SideNavbarItem } from '@/composables/useSideNavbar' */

const props = defineProps({
  /** Whether to show the more button. */
  showMoreButton: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['itemClicked']);

const { showMoreButton } = toRefs(props);

const { t } = useI18n();

const showSideNavbar = useSettingRef('BarraNavegacionLateral.MostrarBarra');
const sideNavbarOrder = useSettingRef('BarraNavegacionLateral.OrdenElementos');

const { items } = useSideNavbar();
const sideNavbarAccess = useSideNavbarAccess();
const screenOrientation = useScreenOrientation();

const itemExit = ref(items.value.find((item) => item.key === 'back'));

const itemsPerPage = ref(4);
const currentPage = ref(1);

/**
 * Determines if an item should be hidden.
 * @param {SideNavbarItem} item - The item to check.
 * @returns {boolean} `true` if the item should be hidden, `false` otherwise.
 */
function isItemHidden(item) {
  if (item.key === 'more' && !showMoreButton.value) {
    return true;
  }

  return !sideNavbarAccess.isItemVisible(item);
}

/**
 * Determines if an item should be hidden after all items have been filtered.
 * @param {SideNavbarItem[]} array - The array of items.
 * @param {SideNavbarItem} item - The item to check.
 * @returns {boolean} `true` if the item should be hidden, `false` otherwise.
 */
function isItemHiddenPostFilter(array, item) {
  const { key } = item;

  if (key === 'next') {
    const topItems = array.filter((topItem) => topItem.position === 'top');
    return topItems.length <= itemsPerPage.value;
  }

  return false;
}

const orderedItems = computed(() => items.value.filter((item) => sideNavbarOrder.value.includes(item.key)));
const nonOrderedItems = computed(() => items.value.filter((item) => !item.settingName));

const filteredItems = computed(() => {
  const allItems = [...orderedItems.value, ...nonOrderedItems.value];

  const initialFilterItems = allItems.filter((item) => !isItemHidden(item)
    && sideNavbarAccess.isItemAccessible(item));
  const postFilterItems = initialFilterItems.filter((item, _index, array) => !isItemHiddenPostFilter(array, item));

  return postFilterItems ?? [];
});

const visibleTopItems = computed(() => {
  if (!Array.isArray(filteredItems.value)) {
    return [];
  }

  const topItems = filteredItems.value.filter((item) => item.position === 'top');
  const startIndex = (currentPage.value - 1) * itemsPerPage.value;
  const endIndex = startIndex + itemsPerPage.value;

  return topItems.slice(startIndex, endIndex);
});

const bottomItems = computed(() => {
  if (!Array.isArray(filteredItems.value)) {
    return [];
  }

  return filteredItems.value.filter((item) => item.position === 'bottom');
});

/**
 * Navigates to a specified route.
 * @param {string | undefined} route - The route to navigate to.
 */
function navigateToRoute(route) {
  if (!route) {
    return;
  }

  f7.views.main?.router.navigate(route, {
    reloadCurrent: true,
    reloadPrevious: true,
  });

  screenOrientation.lockOrientation('landscape');
}

/**  Handles the 'next' button click event. */
function handleNextButtonClick() {
  if (!Array.isArray(filteredItems.value)) {
    return;
  }

  currentPage.value += 1;

  // Check if it's the last page
  const totalTopItems = filteredItems.value.filter((item) => item.position === 'top').length;
  const lastPage = Math.ceil(totalTopItems / itemsPerPage.value);

  // Wrap around to the first page if it's the last page
  if (currentPage.value > lastPage) {
    currentPage.value = 1;
  }
}

/**
 * Emits an event when an item is clicked.
 * @param {SideNavbarItem} item - The item that was clicked.
 */
function handleItemClick(item) {
  const { key, route } = item;

  emit('itemClicked', item);

  if (route) {
    navigateToRoute(route);
  }

  if (key === 'next') {
    handleNextButtonClick();
  } else if (key === 'back') {
    f7.views.main?.router.back();
    screenOrientation.unlockOrientation();
  }
}
</script>

<style scoped lang="scss">
.navigation-bar {
  --f7-button-height: 44px;

  position: fixed;
  display: flex;
  justify-content: center;
  min-width: min(100%, 73px);
  height: calc(100vh - var(--information-bar-height));
  z-index: 100;
  background-color: #ebecf0;
}

.navigation-bar__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: calc(99vh - var(--information-bar-height));
  min-height: 100%;
}

.navigation-bar__content-only {
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: calc(99vh - var(--information-bar-height));
  min-height: 100%;
  margin-bottom: 20px;
}

.navigation-bar__content-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.navigation-bar__content-top {
  gap: 32px;
}

.navigation-bar__content-bottom {
  padding-bottom: calc(var(--f7-typography-padding) * 2);
  gap: 16px;
}

.navigation-bar__separator {
  background-color: var(--his-metal-light);
  width: 20px;
  height: 1px;
}
</style>
